/**
 * JIRA/HubSpot Style Kanban Card
 * Clean, compact card with professional enterprise styling
 * Includes Unified Scenario Planning support
 */
import React from 'react';
import { Calendar, Package, Globe, Eye, User, MoreHorizontal, GripVertical, TrendingUp, TrendingDown, Layers, Workflow } from 'lucide-react';
import { formatCompactCurrency } from '../../api/pipeline';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';

const KanbanCard = ({ 
  opportunity, 
  onClick, 
  isDragging,
  onStageChange,
  onArchive,
  onOpenPlanning,
  onOpenProgram,
  compact = false,
  activeWorkflowsCount = 0
}) => {
  const {
    id,
    program_name,
    oem,
    priority,
    revenue_potential_cents,
    revenue_source,
    confidence_percent,
    sop_date,
    linked_parts_count,
    program_id,
    region,
    assigned_to,
    watchers = [],
    active_scenario,
    active_scenario_id
  } = opportunity;

  // Calculate weighted value
  const weightedValue = revenue_potential_cents && confidence_percent
    ? (revenue_potential_cents * confidence_percent / 100)
    : revenue_potential_cents;

  // Check if revenue needs recalculation
  const needsRecalculation = linked_parts_count > 0 && revenue_source !== 'calculated';

  // Format SOP date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Get priority border color (JIRA style left border)
  const getPriorityBorderColor = () => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'medium': return 'border-l-amber-500';
      case 'low': return 'border-l-slate-400';
      default: return 'border-l-slate-300';
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Prevent click propagation for dropdown
  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      onClick={onClick}
      className={`
        group bg-white rounded-sm mb-2 cursor-pointer
        border border-slate-200 border-l-4 ${getPriorityBorderColor()}
        transition-all duration-200 ease-out
        hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5
        ${isDragging ? 'shadow-xl rotate-2 scale-105 opacity-90' : 'shadow-sm'}
      `}
    >
      {/* Card Content */}
      <div className="p-3">
        {/* Header Row: Title + Actions */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-slate-900 truncate leading-tight" title={program_name}>
              {program_name}
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">{oem}</p>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={handleDropdownClick}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClick?.(); }}>
                  View Details
                </DropdownMenuItem>
                {program_id && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpenProgram?.(opportunity); }}>
                    <Package className="w-4 h-4 mr-2" />
                    Open Program 360
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStageChange?.(id, 'qualified'); }}>
                  Move to Qualified
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStageChange?.(id, 'rfq'); }}>
                  Move to RFQ
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStageChange?.(id, 'quoted'); }}>
                  Move to Quoted
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); onOpenPlanning?.(id); }}
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Open Planning
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={(e) => { e.stopPropagation(); onArchive?.(id); }}
                >
                  Archive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Active Scenario Badge */}
        {(active_scenario?.name || program_id) && (
          <div className="mb-2 flex flex-wrap gap-2">
            {program_id && (
              <Badge
                variant="outline"
                className="text-xs cursor-pointer bg-blue-50 text-blue-700 border-blue-200 gap-1"
                onClick={(e) => { e.stopPropagation(); onOpenProgram?.(opportunity); }}
              >
                <Package className="w-3 h-3" />
                Program 360
              </Badge>
            )}
            {active_scenario && active_scenario.name && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="outline" 
                    className="text-xs cursor-pointer bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"
                    onClick={(e) => { e.stopPropagation(); onOpenPlanning?.(id); }}
                  >
                    <Layers className="w-3 h-3" />
                    {active_scenario.name}
                    {active_scenario.revenue_delta_cents !== 0 && (
                      <span className={active_scenario.revenue_delta_cents >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {active_scenario.revenue_delta_cents >= 0 ? (
                          <TrendingUp className="w-3 h-3 inline ml-0.5" />
                        ) : (
                          <TrendingDown className="w-3 h-3 inline ml-0.5" />
                        )}
                      </span>
                    )}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="text-xs">
                    <p className="font-semibold">Active Scenario: {active_scenario.name}</p>
                    {active_scenario.revenue_delta_cents !== 0 && (
                      <p className={active_scenario.revenue_delta_cents >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        Revenue Impact: {active_scenario.revenue_delta_cents >= 0 ? '+' : ''}{formatCompactCurrency(active_scenario.revenue_delta_cents)}
                        {' '}({active_scenario.revenue_delta_percent >= 0 ? '+' : ''}{active_scenario.revenue_delta_percent}%)
                      </p>
                    )}
                    {active_scenario.coverage_percent > 0 && (
                      <p className="text-slate-500">Coverage: {active_scenario.coverage_percent}% of parts edited</p>
                    )}
                    <p className="text-slate-400 mt-1">Click to open Planning tab</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            )}
          </div>
        )}

        {/* Workflow Status Badge */}
        {activeWorkflowsCount > 0 && (
          <div className="mb-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="outline" 
                    className="text-xs cursor-pointer bg-blue-50 text-blue-700 border-blue-200 gap-1"
                    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
                    data-testid={`workflow-badge-${id}`}
                  >
                    <Workflow className="w-3 h-3" />
                    {activeWorkflowsCount} active workflow{activeWorkflowsCount !== 1 ? 's' : ''}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Click to view workflows</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {/* Value Display */}
        <div className="mb-2">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-bold text-slate-900">
              {formatCompactCurrency(weightedValue)}
            </span>
            {needsRecalculation && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-medium text-amber-700 bg-amber-100 rounded">
                      !
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Revenue should be recalculated from linked parts</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {confidence_percent > 0 && (
            <p className="text-xs text-slate-500">
              {formatCompactCurrency(revenue_potential_cents)} at {confidence_percent}%
            </p>
          )}
        </div>

        {/* Confidence Progress Bar */}
        {confidence_percent > 0 && (
          <div className="mb-3">
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${confidence_percent}%`,
                  backgroundColor: confidence_percent >= 75 ? '#22c55e' : confidence_percent >= 50 ? '#0ea5e9' : '#f59e0b'
                }}
              />
            </div>
          </div>
        )}

        {/* Footer Row: Metadata + Avatars */}
        <div className="flex items-center justify-between">
          {/* Metadata */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {sop_date && (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(sop_date)}</span>
              </div>
            )}
            {linked_parts_count > 0 && (
              <div className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                <span>{linked_parts_count}</span>
              </div>
            )}
            {region && (
              <div className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                <span>{region}</span>
              </div>
            )}
          </div>

          {/* Avatars: Assignee + Watchers */}
          <div className="flex items-center -space-x-1">
            {/* Assignee Avatar */}
            {assigned_to && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Avatar className="w-6 h-6 border-2 border-white ring-1 ring-slate-200">
                      <AvatarFallback className="text-xs bg-blue-500 text-white">
                        {getInitials(assigned_to.name)}
                      </AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Assigned to: {assigned_to.name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            {/* Watcher Count */}
            {watchers && watchers.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center w-6 h-6 bg-slate-100 rounded-full border-2 border-white ring-1 ring-slate-200 text-xs text-slate-600">
                      <Eye className="w-3 h-3" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{watchers.length} watcher{watchers.length !== 1 ? 's' : ''}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
